import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const FrameZoomContext = createContext(null);

const DEFAULT_ZOOM = 0.7;

function readZoom(storageKey, fallback) {
  const raw = sessionStorage.getItem(storageKey);
  if (raw == null || raw === "") return fallback;
  const value = Number(raw);
  return value >= 0.5 && value <= 1.3 ? value : fallback;
}

export function FrameZoomProvider({ storageKey = "dh_frame_zoom_v2", defaultZoom = DEFAULT_ZOOM, children }) {
  const [frameZoom, setFrameZoom] = useState(() => readZoom(storageKey, defaultZoom));

  const nudgeZoom = useCallback(
    (delta) => {
      setFrameZoom((current) => {
        const next = Math.min(1.3, Math.max(0.5, Math.round((current + delta) * 20) / 20));
        sessionStorage.setItem(storageKey, String(next));
        return next;
      });
    },
    [storageKey]
  );

  const value = useMemo(() => ({ frameZoom, nudgeZoom }), [frameZoom, nudgeZoom]);

  useEffect(() => {
    function onFilm(event) {
      const detail = event.detail;
      const zoom = typeof detail === "number" ? detail : Number(detail?.zoom);
      const key = detail && typeof detail === "object" ? detail.key : undefined;
      if (key && key !== storageKey) return;
      if (!(zoom >= 0.5 && zoom <= 1.3)) return;
      sessionStorage.setItem(storageKey, String(zoom));
      setFrameZoom(zoom);
    }
    window.addEventListener("dh-film-zoom", onFilm);
    return () => window.removeEventListener("dh-film-zoom", onFilm);
  }, [storageKey]);

  return <FrameZoomContext.Provider value={value}>{children}</FrameZoomContext.Provider>;
}

export function useFrameZoom() {
  const context = useContext(FrameZoomContext);
  if (!context) {
    throw new Error("useFrameZoom must be used within FrameZoomProvider");
  }
  return context;
}
